import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { UserPlus, Users, Zap } from 'lucide-react';
import {
  Badge,
  Button,
  DataTable,
  DeleteConfirmDialog,
  UserCard,
  createActionsColumn,
  createColumnHeader,
  type DataTableColumnDef
} from '@bliks-io/ui';
import type { CreatePartnerMemberRequest, User } from '@bliks-io/contracts';
import { useOrgMembers } from '@/api/queries/useOrgMembers';
import {
  useDeleteOrgMember,
  useSendOrgMemberInvitation,
  useSuspendOrgMember,
  useUnsuspendOrgMember,
  useUpdateOrgMember
} from '@/api/mutations/useOrgMembers';
import { useCreatePartnerMember } from '@/pages/partner/api/mutations/useCreatePartnerMember';
import { useActivatePartnerSales } from '@/pages/partner/api/mutations/useActivatePartnerSales';
import { usePendingRowIds } from '@/api/mutations/usePendingRowIds';
import { toastApiError } from '@/api/mutations/toast-api-error';
import { orgMemberKeys } from '@/api/query-keys';
import { usePartnerAdminAccess } from './usePartnerAdminAccess';
import { getMemberStatusActions } from '@/components/organization/getMemberStatusActions';
import { getUserStatusLabel, getUserStatusColorScheme } from '@/lib/user-utils';
import { PartnerErrorState } from '@/pages/partner/components/PartnerErrorState';
import { PartnerInfoPanel } from '@/pages/partner/components/PartnerInfoPanel';
import { PartnerPageHeader } from '@/pages/partner/components/PartnerPageHeader';
import { PartnerSettingsTabsNav } from '@/pages/partner/components/PartnerSettingsTabsNav';
import { PartnerMemberDialog } from './PartnerMemberDialog';

export function PartnerTeamPage() {
  const { t } = useTranslation();

  const { isPartnerAdmin, isLoading: loadingRole } = usePartnerAdminAccess();
  const { data: members = [], isLoading, isError, error, refetch, isFetching } = useOrgMembers();

  const createMember = useCreatePartnerMember();
  const activateSales = useActivatePartnerSales();
  const updateMember = useUpdateOrgMember();
  const deleteMember = useDeleteOrgMember();
  const sendInvitation = useSendOrgMemberInvitation();
  const suspend = useSuspendOrgMember();
  const unsuspend = useUnsuspendOrgMember();
  const pendingMemberIds = usePendingRowIds(orgMemberKeys.rowActionMutations());

  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState<'create' | 'edit'>('create');
  const [editingMember, setEditingMember] = useState<User | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const openCreate = () => {
    setEditingMember(null);
    setDialogMode('create');
    setDialogOpen(true);
  };
  const openEdit = (member: User) => {
    setEditingMember(member);
    setDialogMode('edit');
    setDialogOpen(true);
  };

  const handleSubmit = (values: CreatePartnerMemberRequest) => {
    if (dialogMode === 'create') {
      createMember.mutate(values, {
        onSuccess: () => {
          toast.success(t('partner.team.createSuccess'));
          setDialogOpen(false);
        },
        onError: (error) => toastApiError(error, 'partner.team.createError')
      });
      return;
    }
    if (!editingMember) return;
    // Send userTypes only when the stored roles differ from what the form
    // would write: an unchanged role is not an assignment, and the
    // role-lifecycle guard reads every userTypes payload as one (it refuses to
    // strip the last working partner_admin, the actor's own role included).
    // Compared as the whole array, not just the partner role, so a member who
    // also carries a stray customer role is healed by the next edit instead of
    // keeping it forever.
    const storedRoles = editingMember.userTypes;
    const roleChanged = storedRoles.length !== 1 || storedRoles[0] !== values.userType;
    updateMember.mutate(
      {
        id: editingMember._id,
        data: {
          firstName: values.firstName,
          lastName: values.lastName,
          ...(roleChanged ? { userTypes: [values.userType] } : {})
        }
      },
      {
        onSuccess: () => {
          toast.success(t('partner.team.updateSuccess'));
          setDialogOpen(false);
        },
        onError: (error) => toastApiError(error, 'partner.team.updateError')
      }
    );
  };

  // mutateAsync, not mutate(id, { onSuccess }): per-call callbacks fire only for
  // the latest call, so a second row's action would swallow the first's toast.
  const withToasts =
    (
      mutation: { mutateAsync: (id: string) => Promise<unknown> },
      successKey: string,
      errorKey: string
    ) =>
    async (id: string) => {
      try {
        await mutation.mutateAsync(id);
        toast.success(t(successKey));
      } catch (error) {
        toastApiError(error, errorKey);
      }
    };
  const statusHandlers = {
    onSendInvitation: withToasts(
      sendInvitation,
      'partner.team.inviteSuccess',
      'partner.team.inviteError'
    ),
    onSuspend: withToasts(suspend, 'partner.team.suspendSuccess', 'common.Error'),
    onUnsuspend: withToasts(unsuspend, 'partner.team.unsuspendSuccess', 'common.Error')
  };

  const handleActivateSales = async (id: string) => {
    try {
      const res = await activateSales.mutateAsync(id);
      toast.success(
        t(
          res.created
            ? 'partner.team.activateSales.successCreated'
            : 'partner.team.activateSales.successLinked'
        )
      );
    } catch (error) {
      toastApiError(error, 'partner.team.activateSales.error');
    }
  };

  // Not memoized: the action handlers close over per-render mutation objects, so
  // a stable dependency list isn't achievable without churn — and the member
  // table is small.
  const columns: DataTableColumnDef<User>[] = [
    {
      id: 'member',
      header: createColumnHeader(t('partner.team.columns.name')),
      cell: ({ row }) => (
        <UserCard
          user={{
            firstName: row.original.firstName,
            lastName: row.original.lastName,
            email: row.original.email,
            avatar: row.original.avatar
          }}
          variant="with-email"
        />
      ),
      size: 280
    },
    {
      id: 'position',
      header: createColumnHeader(t('partner.team.columns.position')),
      cell: ({ row }) => row.original.jobTitle || '—'
    },
    {
      id: 'role',
      header: createColumnHeader(t('partner.team.columns.role')),
      cell: ({ row }) => (
        <div className="flex flex-wrap gap-1">
          {row.original.userTypes.map((ty) => (
            <Badge key={ty} variant="outline">
              {t(`userTypes.${ty}`)}
            </Badge>
          ))}
        </div>
      )
    },
    {
      accessorKey: 'status',
      header: createColumnHeader(t('partner.team.columns.status')),
      cell: ({ row }) => (
        <Badge colorScheme={getUserStatusColorScheme(row.original.status)}>
          {getUserStatusLabel(row.original.status, t)}
        </Badge>
      )
    },
    {
      id: 'salesStatus',
      header: createColumnHeader(t('partner.team.columns.salesStatus')),
      cell: ({ row }) => {
        const active = Boolean(row.original.hubspotContactId);
        return (
          <Badge colorScheme={active ? 'success' : 'neutral'}>
            {t(active ? 'partner.team.salesStatus.active' : 'partner.team.salesStatus.inactive')}
          </Badge>
        );
      }
    },
    createActionsColumn<User>({
      isRowPending: (member) => pendingMemberIds.has(member._id),
      onEdit: (member) => openEdit(member),
      onDelete: (member) => setDeletingId(member._id),
      getCustomActions: (member) => {
        const actions = getMemberStatusActions(member, statusHandlers, t);
        // Sales activation: only for users not yet linked to a HubSpot contact
        // (without it the n8n lead intake can't attribute the lead), and only
        // once the invite is accepted — the backend refuses earlier, because
        // before first sign-in the email is unproven (PARTNER_MEMBER_NOT_ACTIVE).
        if (!member.hubspotContactId && member.status === 'active') {
          actions.push({
            label: t('partner.team.activateSales.action'),
            icon: <Zap className="w-4 h-4" />,
            onClick: () => handleActivateSales(member._id)
          });
        }
        return actions;
      }
    })
  ];

  const refresh = () => refetch();

  // The route only asserts "is a partner"; user management is partner_admin
  // only, and the profile's role answers that without a request.
  if (!loadingRole && !isPartnerAdmin) {
    return (
      <div className="flex flex-col h-full">
        <PartnerSettingsTabsNav />
        <PartnerInfoPanel
          icon={Users}
          title={t('partner.common.adminOnlyTitle')}
          description={t('partner.team.adminOnlyDescription')}
          className="p-6"
        />
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      <PartnerSettingsTabsNav />
      <div className="flex flex-col flex-1 p-6 gap-6 overflow-auto">
        <PartnerPageHeader
          title={t('organizationSettings.userManagement.title')}
          subtitle={t('organizationSettings.userManagement.description')}
          onRefresh={refresh}
          isRefreshing={isFetching}
        >
          <Button onClick={openCreate}>
            <UserPlus className="w-4 h-4 mr-2" />
            {t('partner.team.addMember')}
          </Button>
        </PartnerPageHeader>

        <section className="space-y-3">
          <h2 className="flex items-center gap-2 text-lg font-medium text-foreground">
            <Users className="w-4 h-4" />
            {t('partner.team.membersTitle')}
          </h2>
          {isError ? (
            <PartnerErrorState
              error={error}
              icon={Users}
              title={t('partner.common.adminOnlyTitle')}
              description={t('partner.team.adminOnlyDescription')}
              onRetry={() => refetch()}
              className="py-10"
            />
          ) : (
            <DataTable
              columns={columns}
              data={members}
              isLoading={isLoading}
              emptyMessage={t('partner.team.membersEmpty')}
            />
          )}
        </section>

        <PartnerMemberDialog
          open={dialogOpen}
          onOpenChange={setDialogOpen}
          mode={dialogMode}
          member={editingMember}
          isSubmitting={createMember.isPending || updateMember.isPending}
          onSubmit={handleSubmit}
        />

        <DeleteConfirmDialog
          open={deletingId !== null}
          onOpenChange={(open) => !open && setDeletingId(null)}
          onConfirm={() => {
            if (!deletingId) return;
            deleteMember.mutate(deletingId, {
              onSuccess: () => {
                toast.success(t('partner.team.deleteSuccess'));
                setDeletingId(null);
              },
              onError: (error) => toastApiError(error, 'common.Error')
            });
          }}
          isDeleting={deleteMember.isPending}
          title={t('partner.team.deleteTitle')}
          description={t('partner.team.deleteDescription')}
        />
      </div>
    </div>
  );
}
